import { encounterCreatureField } from './array-types';
import { normalizePhpListish } from './php-listish';
import { isUuid } from '../janus-engine/reactions/layer-list-ids';

type Row = Record<string, unknown>;
type Refusal = { reason: string; message: string };
type RosterRead = { roster: unknown; pending: Promise<unknown> };
type SkillPage = {
    FateTide?: { objectCache?: Map<string, unknown>; casterCache?: Map<string, unknown>; encounterCache?: Map<string, unknown> };
    ftFindFullNestedItem?: (id: string) => unknown;
    SKILL_CATALOG?: Record<string, { item_type?: string }>;
    fetchAndCacheObject?: (id: string) => Promise<unknown>;
    fetchAndCacheCaster?: (id: string) => Promise<void>;
    fetchAndAdoptEncounterArr: (id: string) => Promise<unknown>;
    ftFetchEncounterWithMutationGuard?: (id: string, read: () => Promise<unknown>) => Promise<unknown>;
    ftRealiasCastersFromEncounterBlob?: (encounter: Row) => void;
    ftSkillRosterReads?: Map<string, RosterRead>;
};
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const memberId = (member: unknown): string => upper(encounterCreatureField(record(member)?.encounter_creatures, 'encounter_creature_id'));

/** Skills can be clicked without anyone holding their definition, or while another room is displayed.
 * Gather their catalog input and the actor's own roster through the existing browser bridges. */
export async function skillRollEncounterId(encounterId: string, actorId: string, abilityId: string): Promise<string | Refusal> {
    const page = window as unknown as SkillPage, abilityKey = upper(abilityId), actorKey = upper(actorId);
    let ability = record(page.ftFindFullNestedItem?.(abilityKey)) ?? record(page.FateTide?.objectCache?.get(abilityKey));
    if (!/_skill$/.test(String(ftItemsCol(ability, 'item_type') ?? page.SKILL_CATALOG?.[abilityKey]?.item_type ?? ''))) return encounterId;
    if (!isUuid(actorKey)) return { reason: 'skill-roll:select-a-character', message: 'Select a character to roll this skill.' };
    if (!record(ability?.items) && page.fetchAndCacheObject) ability = record(await page.fetchAndCacheObject(abilityKey));
    if (record(ability?.items)) page.FateTide?.objectCache?.set(abilityKey, ability);

    const encounter = (id: string): Row | null => record(page.FateTide?.encounterCache?.get(upper(id)));
    const roster = (id: string): unknown[] => normalizePhpListish(encounter(id)?.encounter_creatures_arr);
    if (roster(encounterId).some(member => memberId(member) === actorKey)) return upper(encounterId);
    const heldCaster = (): Row | null => record(page.FateTide?.casterCache?.get(actorKey));
    let caster = heldCaster();
    if (!caster && page.fetchAndCacheCaster) {
        try { await page.fetchAndCacheCaster(actorKey); } catch { /* The encounter read below may still hold the actor. */ }
        caster = heldCaster();
    }
    const ownEncounterId = upper(encounterCreatureField(caster?.encounter_creatures, 'encounter_id')) || upper(encounterId);
    if (ownEncounterId && !roster(ownEncounterId).some(member => memberId(member) === actorKey)) {
        // Share the read across bundles, and retry only when the held roster changes.
        const reads = page.ftSkillRosterReads ??= new Map(), key = `${ownEncounterId}|${actorKey}`;
        let read = reads.get(key);
        if (!read || read.roster !== encounter(ownEncounterId)?.encounter_creatures_arr) {
            const next: RosterRead = { roster: encounter(ownEncounterId)?.encounter_creatures_arr, pending: Promise.resolve() };
            next.pending = Promise.resolve().then(async () => {
                try {
                    // Read + compile without getEncounterArr's current-room pointer setter.
                    const attempt = () => page.fetchAndAdoptEncounterArr(ownEncounterId);
                    const loaded = record(await (page.ftFetchEncounterWithMutationGuard
                        ? page.ftFetchEncounterWithMutationGuard(ownEncounterId, attempt) : attempt()));
                    if (!loaded) throw new Error('Encounter unavailable');
                    page.FateTide?.encounterCache?.set(ownEncounterId, loaded);
                    page.ftRealiasCastersFromEncounterBlob?.(loaded);
                } finally { next.roster = encounter(ownEncounterId)?.encounter_creatures_arr; }
            });
            reads.set(key, next);
            read = next;
        }
        try { await read.pending; } catch {
            return { reason: 'skill-roll:encounter-unavailable', message: 'This character’s encounter could not be loaded. Reload the page and try again.' };
        }
    }
    if (!roster(ownEncounterId).some(member => memberId(member) === actorKey))
        return { reason: 'skill-roll:actor-unavailable', message: 'This character is unavailable in its encounter. Reload the page and try again.' };
    return ownEncounterId;
}
